import { Component, ElementRef, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { CarrierStatus } from '../../../../shared/enums/enum';
import { ErrorHandlerService } from '../../../../shared/services/error-handler.service';
import { MasterService } from '../../../../shared/services/master.service';
import { ToasterService } from '../../../../shared/services/toaster.service';
import { CarrierModel } from '../../models/carriers.model';
import { CarriersApiService } from '../../services/carriers-api.service';

@Component({
  selector: 'app-add-edit-carrier',
  standalone: false,
  templateUrl: './add-edit-carrier.component.html',
  styleUrl: './add-edit-carrier.component.css',
})
export class AddEditCarrierComponent implements OnInit {
  @Output() triggerParentMethod = new EventEmitter();
  @ViewChild('closeBtn') closeBtn?: ElementRef<HTMLElement>;

  validationErrors = {
    carrierEmail: '',
    carrierPhone: '',
    contactEmail: '',
    contactPhone: '',
    zipCode: '',
  };
  frmText: string = 'Add Carrier';
  carrierObj: CarrierModel = new CarrierModel();
  showErrors: boolean = false;
  companyId: number = 0;
  userInfo: any;
  isLoading: boolean = false;
  CarrierStatus = CarrierStatus;
  constructor(
    private carrierService: CarriersApiService,
    private toastService: ToasterService,
    private masterService: MasterService,
    private errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.userInfo = this.masterService.getUserDetailFromStorage();
    this.companyId = Number(this.userInfo.companyId);
  }

  initializeRecord(id: number): void {
    this.carrierObj = new CarrierModel();

    if (id > 0) {
      this.frmText = 'Edit Carrier';
      this.getCarrierById(id);
    } else {
      this.frmText = 'Add Carrier';
    }
  }

  getCarrierById(id: number): void {
    this.carrierService.getCarrierById(id).subscribe({
      next: (response: CarrierModel) => {
        this.carrierObj = new CarrierModel(response);
        this.triggerParentMethod.emit();
      },
      error: (error) => {
        this.errorHandlerService.handleApiError(error);
      },
    });
  }

  allowOnlyNumbers(event: KeyboardEvent): void {
    const charCode = event.which ? event.which : event.keyCode;
    if (charCode < 48 || charCode > 57) {
      event.preventDefault();
    }
  }

  markTouched(field: any): void {
    field.control.markAsTouched();
  }

  onCancel(): void {
    this.triggerParentMethod.emit();
  }

  showRequiredFieldsMessage(event: MouseEvent): void {
    if (this.isSaveDisabled()) {
      event.preventDefault();
      this.toastService.error('Please fill all required fields before saving.');
    }
  }

  isSaveDisabled(): boolean {
    const c = this.carrierObj;
    const pc = c.primaryContact;
    const addr = c.address;

    const isValidEmail = (email: string): boolean => {
      const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
      return valid;
    };

    const isValidPhone = (phone: string): boolean => {
      const valid = /^\+?[0-9]{7,15}$/.test(phone);
      return valid;
    };

    const isValidZip = (zip: string): boolean => {
      const valid = /^[A-Za-z0-9\s\-]{3,10}$/.test(zip);
      return valid;
    };

    // Clear previous errors
    this.validationErrors = {
      carrierEmail: '',
      carrierPhone: '',
      contactEmail: '',
      contactPhone: '',
      zipCode: '',
    };

    // Populate error messages
    if (c.email && !isValidEmail(c.email)) {
      this.validationErrors.carrierEmail = 'Invalid carrier email format';
    }
    if (c.phoneNumber && !isValidPhone(c.phoneNumber)) {
      this.validationErrors.carrierPhone = 'Invalid carrier phone number';
    }
    if (pc.email && !isValidEmail(pc.email)) {
      this.validationErrors.contactEmail = 'Invalid contact email format';
    }
    if (pc.phoneNumber && !isValidPhone(pc.phoneNumber)) {
      this.validationErrors.contactPhone = 'Invalid contact phone number';
    }
    if (addr.zipCode && !isValidZip(addr.zipCode)) {
      this.validationErrors.zipCode = 'Invalid zip code';
    }

    return (
      !c.name ||
      !c.email ||
      !isValidEmail(c.email) ||
      !c.phoneNumber ||
      !isValidPhone(c.phoneNumber) ||
      !pc.firstName ||
      !pc.lastName ||
      !pc.email ||
      !isValidEmail(pc.email) ||
      !pc.phoneNumber ||
      !isValidPhone(pc.phoneNumber) ||
      !addr.line1 ||
      !addr.city ||
      !addr.zipCode ||
      !isValidZip(addr.zipCode) ||
      !addr.stateName ||
      !addr.countryName
    );
  }

  manageCarrierOperation(): void {
    this.carrierObj.companyId = this.companyId;

    if (this.carrierObj.id > 0) {
      this.updateRecord();
    } else {
      this.addNewRecord();
    }
  }

  addNewRecord(): void {
    this.isLoading = true;
    this.carrierObj.address.type = 1;
    this.carrierService.createCarrier(this.carrierObj).subscribe({
      next: () => {
        this.toastService.success('Carrier has been added successfully!');
        this.triggerParentMethod.emit();
        this.closeBtn?.nativeElement.click();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorHandlerService.handleApiError(err);
        this.isLoading = false;
      },
    });
  }

  updateRecord(): void {
    this.isLoading = true;
    const id = this.carrierObj.id;
    this.carrierService.updateCarrier(id, this.carrierObj).subscribe({
      next: () => {
        this.toastService.success('Carrier has been updated successfully!');
        this.triggerParentMethod.emit();
        this.closeBtn?.nativeElement.click();
        this.isLoading = false;
      },
      error: (err) => {
        this.errorHandlerService.handleApiError(err);
        this.isLoading = false;
      },
    });
  }
}
